import useAppConfig from '@/hooks/use-app-config';
import { EnumProductAnalyticsEvent } from '@calm/evexia-analytics';
import {
    DS_COLOR,
    DS_FONT_SIZE,
    DS_FONT_WEIGHT,
    DS_SPACING_UNITS,
} from '@calm/evexia-design-tokens';
import { MediaNestedPack, MediaProgramGuide } from '@calm/evexia-web-sdk';
import Card, { CardProps } from '@rhg/components/card';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { usePackContext } from '@rhg/hooks/use-pack-context';
import { useTrackSessionEvent } from '@rhg/hooks/use-send-amplitude-event';
import commonMessages from '@rhg/lang/messages/common';
import discoverMessages from '@rhg/lang/messages/discover';
import { IconButton, Paper, Theme, SxProps, Box } from '@rhg/libs/material';
import { Meditate } from '@rhg/libs/material/icons';
import { sxAppend } from '@rhg/libs/material/sx';
import Image from '@rhg/libs/next/image';
import { routes } from '@rhg/utils/routes';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import { CardVariant } from '../card/types';
import { dailyPracticeCardWidth } from '../discover-dailies/daily-packs';

const stylePackCard = {
    height: '100%',
    '& h3': {
        fontSize: DS_FONT_SIZE.BUTTON_MEDIUM,
    },
};

const styleCaption = {
    fontWeight: DS_FONT_WEIGHT.MEDIUM,
    '& .MuiTypography-caption:first-child': {
        fontWeight: DS_FONT_WEIGHT.BOLD,
    },
};

const styleDailiesPackCard = {
    height: '100%',
    '& h3': {
        fontWeight: DS_FONT_WEIGHT.BOLD,
    },
    '& span': {
        fontWeight: DS_FONT_WEIGHT.MEDIUM,
    },
};

const styleIconButton = {
    width: '100%',
    borderRadius: 'inherit',
    fontFamily: 'inherit',
    fontSize: DS_FONT_SIZE.BUTTON_MEDIUM,
    fontWeight: (theme: Theme) =>
        theme.palette.mode === 'light'
            ? DS_FONT_WEIGHT.MEDIUM
            : DS_FONT_WEIGHT.BOLD,
    py: 4,
};
const styleQuickNavContent = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',

    '& span': {
        width: '100%',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
};
const styleMobileIconButton = {
    p: 3,
};
const styleFallbackIcon = {
    '& path': {
        fill: (theme: Theme) =>
            theme.palette.mode === 'light'
                ? DS_COLOR.BLACK_80P
                : DS_COLOR.WHITE_80P,
    },
};
const styleQuickNavIcon = {
    '& img': {
        opacity: (theme: Theme) => (theme.palette.mode === 'light' ? 0.6 : 0.8),
        filter: (theme: Theme) =>
            theme.palette.mode === 'light' ? 'invert(0)' : 'invert(1)',
    },
    mb: DS_SPACING_UNITS.BASE,
};

export const CATEGORY_PACK = 'calm-health-snappy-quick-nav';
export const DAILIES_PACK = 'calm-health-dailies-grid';
const CLINICAL_PACK = 'calm-health-clinical-programs-discover';
const GOALS_PACK = 'row-card';

export const PackPackCard = ({
    pack,
    displayType,
    cardVariant = 'carouselSquare',
    cardSx,
    cardContentSx,
    CardProps,
}: {
    pack: MediaNestedPack;
    displayType?: string;
    cardVariant?: CardVariant;
    cardSx?: SxProps;
    cardContentSx?: SxProps;
    CardProps?: Partial<CardProps>;
}) => {
    const {
        NEXT_PUBLIC_DISCOVER_DAILIES_QUICK_NAV_PACK: dailiesQuickNavPackClass,
    } = useAppConfig();
    const { push } = useRouter();
    const { formatMessage } = useIntl();
    const isMobile = useIsMobile();
    const { trackPackItemEvent } = useTrackSessionEvent();
    const packTitle = usePackContext();

    const handleClick = (isDaily = false) => {
        const fakeGuideToGivePackItemName = {
            title: pack?.title,
        };
        trackPackItemEvent({
            eventName: EnumProductAnalyticsEvent.PackItemClicked,
            packTitle: packTitle,
            guide: fakeGuideToGivePackItemName as MediaProgramGuide,
        });

        const isQuickNavDaily = pack.packClass === dailiesQuickNavPackClass;

        if (isDaily && !isQuickNavDaily) {
            push(routes.discoverDailies(pack.packClass));
        } else if (isDaily && isQuickNavDaily) {
            push(routes.discoverDailies());
        } else if (pack.packClass === CLINICAL_PACK) {
            push(routes.discoverClinical);
        } else {
            push(routes.discoverPack(pack.packClass));
        }
    };

    if (displayType === CATEGORY_PACK) {
        const isDailyCategory = pack.packClass === dailiesQuickNavPackClass;
        return (
            <Paper sx={cardSx}>
                <IconButton
                    skipTracking
                    data-cy="QuickNav"
                    onClick={() => handleClick(isDailyCategory)}
                    sx={sxAppend(
                        styleIconButton,
                        isMobile && styleMobileIconButton
                    )}
                >
                    <Box sx={styleQuickNavContent}>
                        <Box sx={styleQuickNavIcon}>
                            {pack.image?.url ? (
                                <Image
                                    src={pack.image?.url ?? ''}
                                    alt=""
                                    width={24}
                                    height={24}
                                />
                            ) : (
                                <Meditate sx={styleFallbackIcon} />
                            )}
                        </Box>
                        <span>{pack?.title}</span>
                    </Box>
                </IconButton>
            </Paper>
        );
    } else if (displayType === DAILIES_PACK) {
        return (
            <Card
                title={pack?.title ?? ''}
                titleComponent="h3"
                categoryTitle={formatMessage(
                    discoverMessages['discover.daily_practice']
                )}
                categoryTitleColor="text.sub"
                titleTopSpacing={2.5}
                titleBottomSpacing={3}
                subtitle={pack?.subtitle ?? ''}
                imageSrc={pack.image?.url ?? ''}
                imageAlt=""
                onClick={() => handleClick(true)}
                label={`${pack?.title} ${formatMessage(
                    commonMessages['common.view_details']
                )}`}
                sx={sxAppend(
                    styleDailiesPackCard,
                    {
                        img: { height: dailyPracticeCardWidth },
                    },
                    cardSx
                )}
                contentSx={sxAppend(styleCaption, cardContentSx)}
                variant={cardVariant}
                isEntireCardClickable
                {...CardProps}
            />
        );
    }

    return (
        <Card
            title={pack?.title ?? ''}
            titleComponent="h3"
            imageSrc={pack.image?.url ?? ''}
            imageAlt=""
            onClick={() => handleClick()}
            label={`${pack?.title} ${formatMessage(
                commonMessages['common.view_details']
            )}`}
            sx={sxAppend(stylePackCard, cardSx)}
            titleTopSpacing={pack?.displayType === GOALS_PACK ? 0 : undefined}
            titleBottomSpacing={
                pack?.displayType === GOALS_PACK ? 0 : undefined
            }
            contentSx={cardContentSx}
            variant={cardVariant}
            isEntireCardClickable
            {...CardProps}
        />
    );
};
